/**
 * MtgCraft JSON backup exporters
 *
 * All MtgCraft-format backup exports (collection, deck, list) live here.
 */
import type { EnrichedCollectionCard, Folder } from '@/types/card';
import type { Deck, DeckCard, DeckZone } from '@/types/deck';
import type { CardList, EnrichedListCard, ListCard } from '@/types/lists';

import type {
  CollectionBackup,
  DeckBackupData,
  DeckCardBackup,
  ListCardBackup,
  MultiDeckBackup,
  MultiListBackup,
  SingleDeckBackup,
  SingleListBackup,
} from '../types';

// ============================================================================
// Collection Backup
// ============================================================================

/**
 * Export full collection as MtgCraft JSON backup
 */
export function exportMtgCraft(cards: EnrichedCollectionCard[], folders: Folder[]): string {
  const backup: CollectionBackup = {
    type: 'mtgcraft_collection',
    version: 1,
    exported_at: new Date().toISOString(),
    cards: cards.map((card) => ({
      id: card.id,
      scryfall_id: card.scryfall_id,
      quantity: card.quantity,
      finish: card.finish,
      condition: card.condition,
      language: card.language,
      purchase_price: card.purchase_price,
      purchase_currency: card.purchase_currency,
      purchase_date: card.purchase_date,
      is_altered: card.is_altered,
      is_playtest: card.is_playtest,
      is_signed: card.is_signed,
      notes: card.notes,
      folder_id: card.folder_id,
      source_system: card.source_system,
      source_id: card.source_id,
    })),
    folders: folders.map((folder) => ({
      id: folder.id,
      name: folder.name,
      color: folder.color,
      icon: folder.icon,
      parent_id: folder.parent_id,
      sort_order: folder.sort_order,
    })),
  };

  return JSON.stringify(backup, null, 2);
}

// ============================================================================
// Deck Backup
// ============================================================================

/** Minimal card data needed for deck backup export */
export interface ExportableCard {
  id: string;
  scryfall_id: string;
  quantity: number;
  zone: DeckZone;
}

/**
 * Export a single deck to MtgCraft backup format
 */
export function exportSingleDeckBackup(deck: Deck, cards: ExportableCard[]): string {
  const backupData = deckToBackupData(deck, cards);

  const backup: SingleDeckBackup = {
    type: 'mtgcraft_deck',
    version: 1,
    exported_at: new Date().toISOString(),
    deck: backupData,
  };

  return JSON.stringify(backup, null, 2);
}

/**
 * Export multiple decks to MtgCraft backup format
 */
export function exportMultiDeckBackup(decks: Array<{ deck: Deck; cards: DeckCard[] }>): string {
  const backupData = decks.map(({ deck, cards }) => deckToBackupData(deck, cards));

  const backup: MultiDeckBackup = {
    type: 'mtgcraft_decks',
    version: 1,
    exported_at: new Date().toISOString(),
    decks: backupData,
  };

  return JSON.stringify(backup, null, 2);
}

// ============================================================================
// List Backup
// ============================================================================

export interface ListWithCards {
  list: CardList;
  cards: EnrichedListCard[] | ListCard[];
}

/**
 * Export a single list as MtgCraft JSON backup
 */
export function exportSingleListBackup(list: CardList, cards: EnrichedListCard[]): string {
  const cardBackups: ListCardBackup[] = cards.map((card) => ({
    oracle_id: card.oracle_id,
    name: card.name,
    scryfall_id: card.scryfall_id || undefined,
    quantity: card.quantity,
    priority: card.priority !== 'medium' ? card.priority : undefined,
    notes: card.notes ?? undefined,
  }));

  const data: SingleListBackup = {
    type: 'mtgcraft_list',
    version: 1,
    exported_at: new Date().toISOString(),
    list: {
      id: list.id,
      name: list.name,
      icon: list.icon ?? undefined,
      color: list.color ?? undefined,
      cards: cardBackups,
    },
  };

  return JSON.stringify(data, null, 2);
}

/**
 * Export multiple lists as a single MtgCraft backup JSON
 */
export function exportMultiListBackup(listsWithCards: ListWithCards[]): string {
  const data: MultiListBackup = {
    type: 'mtgcraft_lists',
    version: 1,
    exported_at: new Date().toISOString(),
    lists: listsWithCards.map(({ list, cards }) => ({
      id: list.id,
      name: list.name,
      icon: list.icon ?? undefined,
      color: list.color ?? undefined,
      cards: cards.map((card): ListCardBackup => ({
        oracle_id: card.oracle_id,
        name: card.name,
        scryfall_id: card.scryfall_id || undefined,
        quantity: card.quantity,
        priority: card.priority !== 'medium' ? card.priority : undefined,
        notes: card.notes ?? undefined,
      })),
    })),
  };

  return JSON.stringify(data, null, 2);
}

// ============================================================================
// Deck Backup Helpers
// ============================================================================

function deckToBackupData(deck: Deck, cards: ExportableCard[]): DeckBackupData {
  const cardBackups: DeckCardBackup[] = cards.map((card) => ({
    id: card.id,
    scryfall_id: card.scryfall_id,
    quantity: card.quantity,
    zone: mapZoneForBackup(card.zone),
  }));

  return {
    id: deck.id,
    name: deck.name,
    format: deck.format ?? 'casual',
    status: deck.status,
    description: deck.description ?? undefined,
    cover_card_id: deck.cover_card_id ?? undefined,
    cards: cardBackups,
  };
}

const KNOWN_BACKUP_ZONES: ReadonlySet<DeckCardBackup['zone']> = new Set([
  'commander',
  'companion',
  'main',
  'sideboard',
  'maybeboard',
  'tokens',
]);

function mapZoneForBackup(zone: string): DeckCardBackup['zone'] {
  return KNOWN_BACKUP_ZONES.has(zone as DeckCardBackup['zone']) ? (zone as DeckCardBackup['zone']) : 'main';
}
